import { EMOTES } from '../../shared/src/index.ts';

/** Píxeles que hay que alejarse del centro para elegir un emoji (más cerca = cancelar). */
const DEAD_ZONE = 26;
/** Radio de la rueda en píxeles (centro de cada burbuja). */
const RADIUS = 82;
/** Margen para que la rueda no se salga de la pantalla. */
const EDGE = RADIUS + 40;

export interface EmoteWheelOptions {
  /** si la rueda puede abrirse ahora (en partida, sin menús ni chat) */
  canOpen: () => boolean;
  /** emoji elegido al soltar la T (índice en `EMOTES`) */
  onPick: (emote: number) => void;
}

/**
 * Rueda de emojis: mantener T la abre centrada en el cursor, se arrastra el ratón
 * hacia el emoji y al soltar la T se usa. Soltar en el centro cancela.
 */
export class EmoteWheel {
  private readonly root: HTMLElement;
  private readonly pointer: HTMLElement;
  private readonly bubbles: HTMLElement[] = [];
  private open = false;
  private centerX = 0;
  private centerY = 0;
  private mouseX = window.innerWidth / 2;
  private mouseY = window.innerHeight / 2;
  private selected = -1;

  constructor(private readonly options: EmoteWheelOptions) {
    const host = document.getElementById('ui') ?? document.body;
    this.root = document.createElement('div');
    this.root.id = 'emote-wheel';
    this.root.hidden = true;
    const ring = document.createElement('div');
    ring.className = 'emote-ring';
    this.pointer = document.createElement('div');
    this.pointer.className = 'emote-pointer';
    const center = document.createElement('div');
    center.className = 'emote-center';
    center.textContent = 'T';
    this.root.append(ring, this.pointer, center);
    EMOTES.forEach((emoji, index) => {
      const angle = (index / EMOTES.length) * Math.PI * 2;
      const bubble = document.createElement('div');
      bubble.className = 'emote-bubble';
      bubble.textContent = emoji;
      bubble.style.left = `${Math.sin(angle) * RADIUS}px`;
      bubble.style.top = `${-Math.cos(angle) * RADIUS}px`;
      this.bubbles.push(bubble);
      this.root.appendChild(bubble);
    });
    host.appendChild(this.root);

    window.addEventListener('pointermove', (event) => {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      if (this.open) this.refresh();
    });
    window.addEventListener('keydown', (event) => {
      if (event.code !== 'KeyT' || event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (!this.options.canOpen()) return;
      event.preventDefault();
      this.show();
    });
    window.addEventListener('keyup', (event) => {
      if (event.code !== 'KeyT' || !this.open) return;
      const pick = this.selected;
      this.hide();
      if (pick >= 0) this.options.onPick(pick);
    });
    window.addEventListener('blur', () => this.hide());
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Cierra sin elegir (p. ej. al pausar o terminar la partida). */
  hide(): void {
    this.open = false;
    this.selected = -1;
    this.root.hidden = true;
  }

  private show(): void {
    this.open = true;
    this.centerX = clamp(this.mouseX, EDGE, window.innerWidth - EDGE);
    this.centerY = clamp(this.mouseY, EDGE, window.innerHeight - EDGE);
    this.root.style.left = `${this.centerX}px`;
    this.root.style.top = `${this.centerY}px`;
    this.root.hidden = false;
    this.refresh();
  }

  private refresh(): void {
    const dx = this.mouseX - this.centerX;
    const dy = this.mouseY - this.centerY;
    const distance = Math.hypot(dx, dy);
    if (distance < DEAD_ZONE) {
      this.selected = -1;
    } else {
      // 0 arriba, en sentido horario (la Y de pantalla crece hacia abajo)
      const angle = Math.atan2(dx, -dy);
      const step = (Math.PI * 2) / EMOTES.length;
      this.selected = ((Math.round(angle / step) % EMOTES.length) + EMOTES.length) % EMOTES.length;
    }
    this.bubbles.forEach((bubble, index) => bubble.classList.toggle('selected', index === this.selected));
    this.pointer.hidden = this.selected < 0;
    if (this.selected >= 0) this.pointer.style.transform = `rotate(${Math.atan2(dx, -dy)}rad)`;
  }
}

function clamp(value: number, min: number, max: number): number {
  return max < min ? (min + max) / 2 : Math.min(max, Math.max(min, value));
}
